"use client";

import { useMemo } from "react";
import { Lightbulb } from "lucide-react";
import { LearningRichContent } from "@/components/student/LearningRichContent";
import {
  lessonSectionBodyGapClass,
  lessonSectionBodyProseClass,
  stripSectionNumbering,
} from "@/components/student/LessonSectionFrame";

export type ProfessionalRemedialSection = {
  section_number: number;
  concept_name: string;
  heading: string;
  misconception: string;
  reexplanation: string;
  domain_scenario: string;
  practical_takeaway: string;
};

export type ProfessionalRemedialLessonData = {
  remedial_title: string;
  domain?: string;
  struggled_concepts?: string[];
  sections: ProfessionalRemedialSection[];
  reflection_question: string;
};

function isValidProfessionalRemedialSection(s: unknown): s is ProfessionalRemedialSection {
  if (typeof s !== "object" || s === null) return false;
  const sec = s as Record<string, unknown>;
  return (
    typeof sec.section_number === "number" &&
    typeof sec.concept_name === "string" &&
    typeof sec.heading === "string" &&
    typeof sec.misconception === "string" &&
    typeof sec.reexplanation === "string" &&
    typeof sec.domain_scenario === "string" &&
    typeof sec.practical_takeaway === "string"
  );
}

export function isProfessionalRemedialData(v: unknown): v is ProfessionalRemedialLessonData {
  if (typeof v !== "object" || v === null) return false;
  const o = v as Record<string, unknown>;
  if (
    typeof o.remedial_title !== "string" ||
    !Array.isArray(o.sections) ||
    typeof o.reflection_question !== "string"
  ) return false;
  // Every section must match the schema — partial validity causes rendering errors
  return o.sections.length > 0 && o.sections.every(isValidProfessionalRemedialSection);
}

interface ProfessionalRemedialLessonContentProps {
  data: ProfessionalRemedialLessonData;
}

export default function ProfessionalRemedialLessonContent({
  data,
}: ProfessionalRemedialLessonContentProps) {
  const sections = useMemo(
    () =>
      data.sections
        .filter(isValidProfessionalRemedialSection)
        .sort((a, b) => a.section_number - b.section_number),
    [data.sections],
  );

  return (
    <article className="learning-content max-w-none prose prose-gray max-w-none">
      {/* Sections */}
      <div className="space-y-10 not-prose">
        {sections.map((section) => (
          <section key={section.section_number} className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {stripSectionNumbering(section.heading)}
            </h2>
            <LearningRichContent
              content={section.misconception}
              className={lessonSectionBodyProseClass}
              segmentGapClassName={lessonSectionBodyGapClass}
            />
            <LearningRichContent
              content={section.reexplanation}
              className={lessonSectionBodyProseClass}
              segmentGapClassName={lessonSectionBodyGapClass}
            />
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center rounded-full bg-emerald-600 text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
                  Workplace scenario
                </span>
              </div>
              <LearningRichContent
                content={section.domain_scenario}
                className={lessonSectionBodyProseClass}
                segmentGapClassName={lessonSectionBodyGapClass}
              />
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <LearningRichContent
                content={section.practical_takeaway}
                className={lessonSectionBodyProseClass}
                segmentGapClassName={lessonSectionBodyGapClass}
              />
            </div>
          </section>
        ))}
      </div>

      {/* Reflection Question */}
      {data.reflection_question && (
        <section className="mt-8 overflow-hidden rounded-xl border border-violet-200/90 bg-gradient-to-b from-violet-50/50 via-white to-white shadow-sm ring-1 ring-violet-100/40 not-prose">
          <div className="border-b border-violet-100/90 bg-white/90 px-3 pb-2 pt-3">
            <div className="flex items-center gap-2.5">
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-600 text-white shadow-sm shadow-violet-600/20"
                aria-hidden
              >
                <Lightbulb className="h-4 w-4" strokeWidth={2} />
              </div>
              <h3 className="m-0 min-w-0 flex-1 text-[15px] font-semibold leading-tight tracking-tight text-slate-900">
                Reflect
              </h3>
            </div>
          </div>
          <div className="px-4 py-3 text-sm leading-relaxed text-slate-700">
            {data.reflection_question}
          </div>
        </section>
      )}
    </article>
  );
}
